import { Get, Post, PostFile, getApiUrl } from '../api'
import { startLoading, stopLoading } from './loaderAction'

export const FETCH_MULTI_SOURCE_REQUEST = 'FETCH_MULTI_SOURCE_REQUEST'
export const FETCH_MULTI_SOURCE_SUCCESS = 'FETCH_MULTI_SOURCE_SUCCESS'
export const FETCH_MULTI_SOURCE_FAILURE = 'FETCH_MULTI_SOURCE_FAILURE'

export const UPLOAD_MULTI_SOURCE_REQUEST = 'UPLOAD_MULTI_SOURCE_REQUEST'
export const UPLOAD_MULTI_SOURCE_SUCCESS = 'UPLOAD_MULTI_SOURCE_SUCCESS'
export const UPLOAD_MULTI_SOURCE_FAILURE = 'UPLOAD_MULTI_SOURCE_FAILURE'


export const createMultiSourceCase =
  (fileInfoId) =>
    async (dispatch, getState) => {
      const { user } = getState().authReducer
      try {
        dispatch(startLoading())
        const res = await Post(
          `${getApiUrl()}/multiSource/caseCreate/${fileInfoId}`,
          {},
          user,
        )
        return Promise.resolve(res?.data)
      } catch (err) {
        return Promise.reject(err)
      } finally {
        dispatch(stopLoading())
      }
    }

export const fetchMultiSource =
  ({
    page = 1,
    limit = 10,
    search = '',
    status = '',
    sortOrder = 'desc',
  } = {}) =>
    async (dispatch, getState) => {
      const { user } = getState().authReducer
      try {
        dispatch(startLoading())
        dispatch({
          type: FETCH_MULTI_SOURCE_REQUEST,
          meta: { page, limit, search, status, sortOrder },
        })

        let url = `${getApiUrl()}/multiSource/batches?page=${page}&limit=${limit}&search=${encodeURIComponent(
          search,
        )}&sortOrder=${sortOrder}`
        if (status) {
          url += `&status=${encodeURIComponent(status)}`
        }

        const res = await Get(url, user)
        if (!res.success || !res.data?.success) {
          throw (
            res?.error?.message ||
            res?.data?.message ||
            'Failed to load extraction batches'
          )
        }

        const payload = res.data?.data || res.data || {}
        const rows = Array.isArray(payload.data) ? payload.data : []
        const total = Number(payload.total || 0)
        dispatch({
          type: FETCH_MULTI_SOURCE_SUCCESS,
          payload: { rows, total, page, limit, search, status, sortOrder },
        })
      } catch (err) {
        dispatch({
          type: FETCH_MULTI_SOURCE_FAILURE,
          error: err?.toString() || 'Failed to load extraction batches',
        })
        return Promise.reject(err)
      } finally {
        dispatch(stopLoading())
      }
    }

// Upload a ZIP and trigger extraction, then return response
export const uploadMultiSourceZip = (file) => async (dispatch, getState) => {
  const { user } = getState().authReducer
  dispatch({ type: UPLOAD_MULTI_SOURCE_REQUEST })
  try {
    dispatch(startLoading())
    const formData = new FormData()
    formData.append('file', file)

    const res = await PostFile(
      `${getApiUrl()}/multiSource/pipeline/upload`,
      formData,
      user,
    )
    if (!res.success) {
      throw res.error || new Error('Extraction failed')
    }

    const payload = res.data
    dispatch({ type: UPLOAD_MULTI_SOURCE_SUCCESS, payload })
    return payload
  } catch (err) {
    dispatch({
      type: UPLOAD_MULTI_SOURCE_FAILURE,
      error: err?.message || 'Upload failed',
    })
    return Promise.reject(err)
  } finally {
    dispatch(stopLoading())
  }
}

// Re-extract an entire batch, then refresh the current page
export const reExtractBatch = (batchId) => async (dispatch, getState) => {
  const state = getState().multiSourceExtraction || {}
  const page = state.page || 1
  const limit = state.limit || 10
  const search = state.search || ''

  const { user } = getState().authReducer
  try {
    dispatch(startLoading())
    const res = await Post(
      `${getApiUrl()}/multiSource/reExtractBatch`,
      { batchId },
      user,
    )
    if (!res.success) {
      throw res.error?.message || 'Failed to re-extract batch'
    }
    return dispatch(fetchMultiSource({ page, limit, search }))
  } catch (err) {
    return Promise.reject(err)
  } finally {
    dispatch(stopLoading())
  }
}

// Re-extract a single file (does NOT auto-refresh the list, no global loading)
// Accepts optional AbortController signal for cancellation
// Returns { success: true } on success, { cancelled: true } if cancelled
export const reExtractFile =
  (batchId, file_name, options = {}) =>
    async (dispatch, getState) => {
      const { user } = getState().authReducer
      try {
        // Note: No startLoading() - we use local loading state to avoid table re-render
        const res = await Post(
          `${getApiUrl()}/multiSource/reExtractFile`,
          { batchId, file_name },
          user,
          'application/json',
          { signal: options.signal },
        )
        // Check if cancelled
        if (res.cancelled) {
          return { cancelled: true }
        }
        if (!res.success) {
          throw res.error?.message || 'Failed to re-extract file'
        }
        // Return success - component will refresh the list when appropriate
        return { success: true, data: res.data }
      } catch (err) {
        return Promise.reject(err)
      }
      // Note: No stopLoading() - we use local loading state
    }

export const saveMultiSourceFileSummary =
  (payload = {}) =>
    async (dispatch, getState) => {

      const state = getState().multiSourceExtraction || {}
      const page = state.page || 1
      const limit = state.limit || 10
      const search = state.search || ''
      const { user } = getState().authReducer
      try {
        dispatch(startLoading())
        const res = await Post(
          `${getApiUrl()}/multiSource/fileSummary/save`,
          payload,
          user,
        )
        if (!res.success || !res.data?.success) {
          throw (
            res?.error?.message ||
            res?.data?.message ||
            'Failed to save file summary'
          )
        }
        await dispatch(fetchMultiSource({ page, limit, search }))
        return res?.data?.data || res?.data
      } catch (err) {
        return Promise.reject(err)
      } finally {
        dispatch(stopLoading())
      }
    }

// Cancel file extraction - calls backend to mark file as cancelled
export const cancelFileExtraction =
  (batchId, file_name) => async (dispatch, getState) => {
    const { user } = getState().authReducer
    try {
      const res = await Post(
        `${getApiUrl()}/multiSource/cancelExtraction`,
        { batchId, file_name },
        user,
      )
      if (!res.success || !res.data?.success) {
        throw (
          res?.error?.message ||
          res?.data?.message ||
          'Failed to cancel extraction'
        )
      }
      return { success: true, data: res.data?.data }
    } catch (err) {
      return Promise.reject(err)
    }
  }

// Cancel entire batch extraction - calls backend to cancel all processing files
export const cancelBatchExtraction =
  (batchId) => async (dispatch, getState) => {
    const { user } = getState().authReducer
    try {
      const res = await Post(
        `${getApiUrl()}/multiSource/cancelBatchExtraction`,
        { batchId },
        user,
      )
      if (!res.success || !res.data?.success) {
        throw (
          res?.error?.message ||
          res?.data?.message ||
          'Failed to cancel batch extraction'
        )
      }
      return { success: true, data: res.data?.data }
    } catch (err) {
      return Promise.reject(err)
    }
  }
